import CopyIcon from "../../assets/icons/CopyIcon";
import LeftArrowIcon from "../../assets/icons/LeftArrowIcon";
import RightArrowIcon from "../../assets/icons/RightArrowIcon";
import CustomModal from "../../components/common/CustomModal/CustomModal";
import Loader from "../../components/common/Loader";
import gluedin from "gluedin-shorts-js";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  SupportChatMessage,
  SupportTicket,
  extractChats,
  extractTicketDetail,
  getApiErrorMessage,
  getRelativeTime,
  getStatusLabel,
  isApiSuccess,
  isTicketActive,
  mapTicket,
  normalizeSupportStatus,
} from "../support/supportUtils";

const supportModule = new gluedin.GluedInSupportModule();
const CHAT_PAGE_LIMIT = 20;

const getResponseRoot = (response: any) =>
  response?.data?.result ??
  response?.data?.data ??
  response?.data ??
  response?.result ??
  response ??
  null;

const resolveChatsHasMore = (
  response: any,
  receivedCount: number,
  limit: number,
  offset: number,
) => {
  const root = getResponseRoot(response);
  const pagination = root?.pagination || root?.pageInfo || root?.meta || {};
  const hasMoreFlag =
    root?.hasMore ?? pagination?.hasMore ?? pagination?.has_next;

  if (typeof hasMoreFlag === "boolean") return hasMoreFlag;

  const totalPages = Number(
    pagination?.totalPages ?? pagination?.pages ?? root?.totalPages,
  );
  const currentPage = Number(
    pagination?.currentPage ?? pagination?.page ?? root?.page ?? offset,
  );
  if (
    Number.isFinite(totalPages) &&
    totalPages > 0 &&
    Number.isFinite(currentPage)
  ) {
    return currentPage < totalPages;
  }

  const total = Number(
    pagination?.total ??
      pagination?.totalCount ??
      root?.total ??
      root?.count ??
      root?.totalCount,
  );
  if (Number.isFinite(total) && total >= 0) {
    return offset * limit < total;
  }

  return receivedCount >= limit;
};

const toMessageTimestamp = (value: string | number | null) => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
};

const buildMessageIdentity = (message: SupportChatMessage, fallbackIndex = 0) => {
  const stableId = String(message?.id || "").trim();
  if (stableId && !stableId.startsWith("local-")) {
    return `id-${stableId}`;
  }
  const signatureSecond = Math.floor(toMessageTimestamp(message.createdAt) / 1000);
  const signature = `${message.sender}-${message.message.trim()}-${signatureSecond || 0}`;
  if (stableId.startsWith("local-")) {
    return signature;
  }
  return `${signature}-${fallbackIndex}`;
};

const mergeChatCollections = (
  current: SupportChatMessage[],
  incoming: SupportChatMessage[],
) => {
  if (!incoming.length) return current;
  const map = new Map<string, SupportChatMessage>();

  current.forEach((message, index) => {
    map.set(buildMessageIdentity(message, index), message);
  });
  incoming.forEach((message, index) => {
    map.set(buildMessageIdentity(message, current.length + index), message);
  });

  return Array.from(map.values()).sort(
    (first, second) =>
      toMessageTimestamp(first.createdAt) - toMessageTimestamp(second.createdAt),
  );
};

const SupportIssueDetails = () => {
  const navigate = useNavigate();
  const { ticketId = "" } = useParams();
  const { t } = useTranslation();
  const defaultLanguage = localStorage.getItem("defaultLanguage");

  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [chatMessages, setChatMessages] = useState<SupportChatMessage[]>([]);
  const [chatOffset, setChatOffset] = useState(1);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [isLoadingMoreChats, setIsLoadingMoreChats] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showCloseIssueModal, setShowCloseIssueModal] = useState(false);
  const chatListRef = useRef<HTMLDivElement | null>(null);
  const shouldAutoScrollToBottomRef = useRef(false);
  const shouldStickToBottomRef = useRef(true);
  const pendingPrependScrollRef = useRef<{
    previousHeight: number;
    previousTop: number;
  } | null>(null);

  const resolveLabel = (key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const mergeMessages = useCallback(
    (ticketData: SupportTicket | null, chats: SupportChatMessage[]) => {
      const base: SupportChatMessage[] = [];
      if (ticketData?.description) {
        base.push({
          id: `ticket-description-${ticketData.id}`,
          message: ticketData.description,
          sender: "user",
          createdAt: ticketData.createdAt,
          raw: {},
        });
      }
      return mergeChatCollections(base, chats);
    },
    [],
  );

  const messages = useMemo(
    () => mergeMessages(ticket, chatMessages),
    [chatMessages, mergeMessages, ticket],
  );

  const fetchTicketData = useCallback(async () => {
    if (!ticketId) return;
    setIsLoading(true);
    setErrorMessage("");
    try {
      const [ticketResponse, chatResponse] = await Promise.all([
        supportModule.getTicketDetail(ticketId),
        supportModule.getChatList({
          ticketId,
          limit: CHAT_PAGE_LIMIT,
          offset: 1,
        }),
      ]);

      const parsedTicket = extractTicketDetail(ticketResponse, ticketId);
      const parsedChats = extractChats(chatResponse);
      const hasSuccess = isApiSuccess(ticketResponse) || isApiSuccess(chatResponse);

      if (!parsedTicket && !hasSuccess) {
        setErrorMessage(
          getApiErrorMessage(
            ticketResponse,
            resolveLabel(
              "label-support-issue-error",
              "Unable to fetch issue details right now.",
            ),
          ),
        );
        setTicket(null);
        setChatMessages([]);
        setChatOffset(1);
        setHasMoreChats(false);
        return;
      }

      const fallbackTicket =
        parsedTicket ||
        mapTicket({
          ticketId,
          title: resolveLabel("label-issue", "Issue"),
          status: "OPEN",
        });

      setTicket(fallbackTicket);
      setChatMessages(parsedChats);
      setChatOffset(1);
      setHasMoreChats(
        resolveChatsHasMore(chatResponse, parsedChats.length, CHAT_PAGE_LIMIT, 1),
      );
      shouldAutoScrollToBottomRef.current = true;
    } catch (error: any) {
      setErrorMessage(
        getApiErrorMessage(
          error?.response || error,
          resolveLabel(
            "label-support-issue-error",
            "Unable to fetch issue details right now.",
          ),
        ),
      );
      setTicket(null);
      setChatMessages([]);
      setChatOffset(1);
      setHasMoreChats(false);
    } finally {
      setIsLoading(false);
    }
  }, [ticketId, t]);

  useEffect(() => {
    fetchTicketData();
  }, [fetchTicketData]);

  const loadOlderMessages = useCallback(async () => {
    if (!ticketId || isLoading || isLoadingMoreChats || !hasMoreChats) return;

    const listElement = chatListRef.current;
    if (listElement) {
      pendingPrependScrollRef.current = {
        previousHeight: listElement.scrollHeight,
        previousTop: listElement.scrollTop,
      };
    }

    setIsLoadingMoreChats(true);
    const nextOffset = chatOffset + 1;
    try {
      const response = await supportModule.getChatList({
        ticketId,
        limit: CHAT_PAGE_LIMIT,
        offset: nextOffset,
      });
      const parsedChats = extractChats(response);
      setChatMessages((previous) => mergeChatCollections(previous, parsedChats));
      setChatOffset(nextOffset);
      setHasMoreChats(
        resolveChatsHasMore(
          response,
          parsedChats.length,
          CHAT_PAGE_LIMIT,
          nextOffset,
        ),
      );
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingMoreChats(false);
    }
  }, [chatOffset, hasMoreChats, isLoading, isLoadingMoreChats, ticketId]);

  const handleChatListScroll = useCallback(() => {
    const listElement = chatListRef.current;
    if (!listElement) return;

    const distanceFromBottom =
      listElement.scrollHeight - listElement.scrollTop - listElement.clientHeight;
    shouldStickToBottomRef.current = distanceFromBottom <= 80;

    if (listElement.scrollTop <= 40) {
      loadOlderMessages();
    }
  }, [loadOlderMessages]);

  useLayoutEffect(() => {
    const listElement = chatListRef.current;
    if (!listElement) return;

    if (pendingPrependScrollRef.current) {
      const { previousHeight, previousTop } = pendingPrependScrollRef.current;
      const nextScrollTop =
        listElement.scrollHeight - previousHeight + previousTop;
      listElement.scrollTop = Math.max(0, nextScrollTop);
      pendingPrependScrollRef.current = null;
      return;
    }

    if (
      shouldAutoScrollToBottomRef.current ||
      shouldStickToBottomRef.current
    ) {
      listElement.scrollTop = listElement.scrollHeight;
      shouldAutoScrollToBottomRef.current = false;
    }
  }, [isLoading, messages]);

  const copyToClipboard = async (value: string) => {
    if (!value) return;
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }
    const textArea = document.createElement("textarea");
    textArea.value = value;
    textArea.setAttribute("readonly", "true");
    textArea.style.position = "absolute";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  };

  const normalizedStatus = useMemo(
    () => normalizeSupportStatus(ticket?.status),
    [ticket?.status],
  );
  const statusLabel = getStatusLabel(normalizedStatus);
  const isActive = isTicketActive(normalizedStatus);
  const statusClass =
    normalizedStatus === "RESOLVED"
      ? "support-status-chip--resolved"
      : normalizedStatus === "CLOSED"
        ? "support-status-chip--closed"
        : "support-status-chip--open";

  const handleSendMessage = async () => {
    if (!ticket?.id || isSending || !isActive) return;
    const message = inputValue.trim();
    setInputError("");
    if (!message) {
      setInputError(
        resolveLabel(
          "label-support-message-required",
          "Please type a message before sending.",
        ),
      );
      return;
    }

    let optimisticId = "";
    try {
      setIsSending(true);
      const nowIso = new Date().toISOString();
      optimisticId = `local-${Date.now()}`;
      const optimisticMessage: SupportChatMessage = {
        id: optimisticId,
        message,
        sender: "user",
        createdAt: nowIso,
        raw: {
          local: true,
          actionType: "SEND_MESSAGE",
        },
      };
      setChatMessages((previous) =>
        mergeChatCollections(previous, [optimisticMessage]),
      );
      setInputValue("");
      shouldAutoScrollToBottomRef.current = true;

      const response = await supportModule.sendMessage({
        actionType: "SEND_MESSAGE",
        ticketId: ticket.id,
        message,
      });
      if (!isApiSuccess(response)) {
        setChatMessages((previous) =>
          previous.filter((item) => item.id !== optimisticId),
        );
        setInputValue(message);
        setInputError(
          getApiErrorMessage(
            response,
            resolveLabel(
              "label-support-send-error",
              "Unable to send message right now.",
            ),
          ),
        );
        return;
      }

      const parsedSentMessages = extractChats(response);
      if (parsedSentMessages.length) {
        setChatMessages((previous) =>
          mergeChatCollections(previous, parsedSentMessages),
        );
      }

      setInputError("");
    } catch (error: any) {
      if (optimisticId) {
        setChatMessages((previous) =>
          previous.filter((item) => item.id !== optimisticId),
        );
      }
      setInputValue(message);
      setInputError(
        getApiErrorMessage(
          error?.response || error,
          resolveLabel(
            "label-support-send-error",
            "Unable to send message right now.",
          ),
        ),
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleCloseIssue = async () => {
    if (!ticket?.id || isClosing || !isActive) return;
    setInputError("");
    try {
      setIsClosing(true);
      const response = await supportModule.sendMessage({
        actionType: "RESOLVE",
        ticketId: ticket.id,
        message: resolveLabel(
          "label-support-close-message",
          "Issue has been closed by user.",
        ),
      });
      if (!isApiSuccess(response)) {
        setInputError(
          getApiErrorMessage(
            response,
            resolveLabel(
              "label-support-close-error",
              "Unable to close this issue right now.",
            ),
          ),
        );
        return;
      }
      const closedMessage = resolveLabel(
        "label-support-closed-msg",
        "Issue has been closed",
      );
      const closedAt = new Date().toISOString();
      setTicket((previous) =>
        previous
          ? {
              ...previous,
              status: "CLOSED",
              updatedAt: closedAt,
            }
          : previous,
      );
      setChatMessages((previous) =>
        mergeChatCollections(previous, [
          {
            id: `local-close-${Date.now()}`,
            message: closedMessage,
            sender: "system",
            createdAt: closedAt,
            raw: {
              local: true,
              actionType: "RESOLVE",
            },
          },
        ]),
      );
      shouldAutoScrollToBottomRef.current = true;
      setShowCloseIssueModal(false);
    } catch (error: any) {
      setInputError(
        getApiErrorMessage(
          error?.response || error,
          resolveLabel(
            "label-support-close-error",
            "Unable to close this issue right now.",
          ),
        ),
      );
    } finally {
      setIsClosing(false);
    }
  };

  return (
    <div className="full-sec"><div className="right-sec profile-content-list support-page support-issue-page">
        <div className="full-box support-full-box">
          <div className="back-header support-header">
            <ul className="list list-inline">
              <li className="back-btn">
                <button
                  className="back-btn-a"
                  type="button"
                  onClick={() => navigate(-1)}
                >
                  {defaultLanguage === "en" ? (
                    <LeftArrowIcon />
                  ) : (
                    <RightArrowIcon />
                  )}
                  {t("back-btn")}
                </button>
              </li>
              <li className="common-heading">
                {resolveLabel("label-issue-details", "Issue Details")}
              </li>
              <li className="support-header__action">
                {isActive && (
                  <button
                    type="button"
                    className="support-close-btn"
                    onClick={() => setShowCloseIssueModal(true)}
                    disabled={isClosing}
                  >
                    {isClosing ? (
                      <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                    ) : (
                      <i className="fa fa-check" aria-hidden="true" />
                    )}
                    {resolveLabel("label-close", "Close")}
                  </button>
                )}
              </li>
            </ul>
          </div>

          {isLoading && <Loader />}

          {!isLoading && (
            <div className="support-issue-thread">
              {errorMessage && (
                <div className="support-error-block">
                  <p>{errorMessage}</p>
                  <button
                    type="button"
                    className="support-error-block__retry"
                    onClick={fetchTicketData}
                  >
                    {resolveLabel("label-retry", "Retry")}
                  </button>
                </div>
              )}

              {!errorMessage && ticket && (
                <>
                  <article className="support-ticket-card support-ticket-card--summary">
                    <div className="support-ticket-card__top">
                      <div className="support-ticket-card__transaction">
                        {resolveLabel("label-transaction-id", "Transaction ID:")}{" "}
                        {ticket.transactionId || "-"}
                        {ticket.transactionId && (
                          <button
                            type="button"
                            className="support-ticket-card__copy"
                            onClick={() => copyToClipboard(ticket.transactionId)}
                            aria-label={resolveLabel(
                              "label-copy-transaction-id",
                              "Copy transaction ID",
                            )}
                          >
                            <CopyIcon />
                          </button>
                        )}
                      </div>
                      <span className={`support-status-chip ${statusClass}`}>
                        {normalizedStatus === "RESOLVED" && (
                          <i className="fa fa-check-circle" aria-hidden="true" />
                        )}
                        {(normalizedStatus === "OPEN" ||
                          normalizedStatus === "IN_PROGRESS") && (
                          <i className="fa fa-clock-o" aria-hidden="true" />
                        )}
                        {normalizedStatus === "CLOSED" && (
                          <i className="fa fa-ban" aria-hidden="true" />
                        )}
                        {statusLabel}
                      </span>
                    </div>
                    <h4 className="support-ticket-card__title">{ticket.title}</h4>
                    <div className="support-ticket-card__meta">
                      <span>{getRelativeTime(ticket.createdAt, t)}</span>
                    </div>
                  </article>

                  <div
                    ref={chatListRef}
                    className="support-chat-list"
                    onScroll={handleChatListScroll}
                  >
                    {isLoadingMoreChats && (
                      <div className="support-chat-pagination-loading">
                        <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                      </div>
                    )}
                    {messages.map((message, index) => {
                      const isUser = message.sender === "user";
                      const isSystem = message.sender === "system";
                      return (
                        <div
                          key={message.id || `message-${index}`}
                          className={`support-chat-row ${
                            isUser ? "support-chat-row--user" : "support-chat-row--support"
                          }`}
                        >
                          <div
                            className={`support-chat-bubble ${
                              isUser
                                ? "support-chat-bubble--user"
                                : isSystem
                                  ? "support-chat-bubble--system"
                                  : "support-chat-bubble--support"
                            }`}
                          >
                            {!isUser && !isSystem && (
                              <div className="support-chat-bubble__sender">
                                {resolveLabel("label-support-team", "Support Team")}
                              </div>
                            )}
                            <div className="support-chat-bubble__message">
                              {message.message}
                            </div>
                            <div className="support-chat-bubble__time">
                              {getRelativeTime(message.createdAt, t)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {normalizedStatus === "RESOLVED" && (
                    <div className="support-success-banner support-success-banner--resolved">
                      <div className="support-success-banner__icon">
                        <i className="fa fa-check" aria-hidden="true" />
                      </div>
                      <div>
                        <h4>
                          {resolveLabel(
                            "label-support-resolved-msg",
                            "The issue has been resolved.",
                          )}
                        </h4>
                        <p>
                          {resolveLabel(
                            "label-support-resolved-sub",
                            "Please check your wallet for the updated balance.",
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  {normalizedStatus === "CLOSED" && (
                    <div className="support-closed-banner">
                      {resolveLabel("label-support-closed-msg", "Issue has been closed")}
                    </div>
                  )}

                  {inputError && (
                    <div className="support-inline-error">
                      <p>{inputError}</p>
                    </div>
                  )}

                  {isActive && (
                    <div className="support-message-input">
                      <textarea
                        placeholder={resolveLabel(
                          "label-type-message",
                          "Type your message",
                        )}
                        value={inputValue}
                        onChange={(event) => setInputValue(event.target.value)}
                        rows={2}
                        maxLength={500}
                        onKeyDown={(event) => {
                          if (
                            event.key === "Enter" &&
                            !event.shiftKey &&
                            !isSending
                          ) {
                            event.preventDefault();
                            handleSendMessage();
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleSendMessage}
                        disabled={isSending}
                        aria-label={resolveLabel(
                          "label-send-message",
                          "Send message",
                        )}
                      >
                        {isSending ? (
                          <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                        ) : (
                          <i className="fa fa-paper-plane-o" aria-hidden="true" />
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <CustomModal
        isOpen={showCloseIssueModal}
        close={() => {
          if (isClosing) return;
          setShowCloseIssueModal(false);
        }}
        classes="support-close-issue-modal"
      >
        <div className="support-close-modal__content">
          <h3 className="support-close-modal__title">
            {resolveLabel("label-support-close-title", "Close this issue?")}
          </h3>
          <p className="support-close-modal__subtitle">
            {resolveLabel(
              "label-support-close-subtitle",
              "Are you sure you want to close this issue? This will mark it as resolved. You can always open a new issue if you need further assistance.",
            )}
          </p>
          <button
            type="button"
            className="support-close-modal__action"
            onClick={handleCloseIssue}
            disabled={isClosing}
          >
            {isClosing ? (
              <i className="fa fa-spinner fa-spin" aria-hidden="true" />
            ) : (
              resolveLabel("label-support-close-confirm", "Close Issue")
            )}
          </button>
        </div>
      </CustomModal>
    </div>
  );
};

export default SupportIssueDetails;
